/**
 * CriticalDamageService — looks up critical damage effects from the tables.
 *
 * 16 tables: 4 damage types (energy, explosive, impact, rending) × 4 locations (arm, body, head, leg).
 * Severity 1-9 have specific effects; 10+ uses the severity 10 entry.
 */

import criticalData from '../data/critical-damage-tables.json';
import { DamageType } from '../types/dice';

export interface CriticalEffect {
  severity: number;
  effect: string;
  fatigue: number;
  fatigueRoll?: string;
  conditions: string[];
  permanent: boolean;
  permanentEffect?: string;
  death?: boolean;
  testOrDeath?: boolean;
  testOrCondition?: string;
}

type LocationKey = 'arm' | 'body' | 'head' | 'leg';

const tables = (criticalData as any).tables as Record<
  DamageType,
  Record<LocationKey, CriticalEffect[]>
>;

function mapHitLocation(location: string): LocationKey {
  const loc = location.toLowerCase();
  if (loc.includes('arm')) return 'arm';
  if (loc.includes('head')) return 'head';
  if (loc.includes('leg')) return 'leg';
  return 'body';
}

export class CriticalDamageService {
  /**
   * Look up the critical effect for a given damage type, hit location, and severity.
   * Severity is clamped to 1-10 (10+ uses the severity 10 entry).
   *
   * Takes a DamageType rather than a string: callers hold a resolved damage type,
   * so there is nothing to normalise and no unrecognised value to default.
   */
  static lookup(
    damageType: DamageType,
    hitLocation: string,
    severity: number
  ): CriticalEffect | null {
    const locKey = mapHitLocation(hitLocation);

    const table = tables[damageType]?.[locKey];
    if (!table || table.length === 0) return null;

    const clampedSeverity = Math.max(1, Math.min(severity, 10));
    const entry = table.find((e) => e.severity === clampedSeverity);
    return entry || table[table.length - 1]; // 10+ uses the severity 10 entry
  }

  /** Get all entries for a given damage type and location (for reference display). */
  static getTable(damageType: DamageType, hitLocation: string): CriticalEffect[] {
    const locKey = mapHitLocation(hitLocation);
    return tables[damageType]?.[locKey] || [];
  }

  /** Get all available damage types. */
  static getDamageTypes(): DamageType[] {
    return Object.values(DamageType);
  }

  /** Get all available locations. */
  static getLocations(): LocationKey[] {
    return ['arm', 'body', 'head', 'leg'];
  }
}
